import React, { useState, forwardRef, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./style.module.scss";
import { Icons } from "assets";
import classNames from "classnames";
import moment from "moment";

interface CustomInputProps {
  value?: string;
  onClick?: () => void;
  className?: string;
  doubleLabel?: boolean;
}

interface RangeDatePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  setStartDate: (val: Date | null) => void;
  setEndDate: (val: Date | null) => void;
  labelStyle?: any;
  doubleLabel?: boolean;
  noDateSelected?: boolean;
}

function RangeDatePicker({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  labelStyle,
  doubleLabel = false,
  noDateSelected,
}: RangeDatePickerProps) {
  const [label, setLabel] = useState<string>("");

  useEffect(() => {
    const today = new Date();
    const pastDate = new Date(today);
    pastDate.setDate(today.getDate() - 28);

    setStartDate(pastDate);
    setEndDate(today);
    setLabel(`Last 28 days`);
    // eslint-disable-next-line
  }, []);

  const onChangeDate = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    if (start && end) {
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end date

      const today = new Date();
      if (doubleLabel) {
        if (end.toDateString() === today.toDateString()) {
          setLabel(`Last ${diffDays} days`);
        } else {
          setLabel(`${diffDays} days`);
        }
      } else {
        setLabel(
          `${start.toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
          })} - ${end.toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
          })}`
        );
      }
    } else {
      setLabel("");
    }
  };

  const Button = forwardRef<HTMLButtonElement, CustomInputProps>(
    ({ value, onClick, className }, ref) => (
      <button
        className={classNames(styles.btn, labelStyle && labelStyle)}
        onClick={onClick}
        ref={ref}
      >
        {noDateSelected ? (
          <div>
            <label className={styles.dateRangeLabel}>No Date Selected</label>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "end",
              flexDirection: "column",
            }}
          >
            {doubleLabel && (
              <label className={styles.dateRangeLabel}>
                {startDate && moment(startDate).format("DD MMM, YYYY")} -{" "}
                {endDate && moment(endDate).format("DD MMM, YYYY")}
              </label>
            )}
            <label>{label}</label>
          </div>
        )}
        <Icons.DropDownIcon2 />
      </button>
    )
  );

  return (
    <>
      <DatePicker
        customInput={
          <Button
            className="example-custom-input"
            value={label}
            doubleLabel={doubleLabel}
          />
        }
        dateFormat="d MMM"
        selected={startDate}
        onChange={(dates: [Date | null, Date | null]) => onChangeDate(dates)}
        startDate={startDate}
        endDate={endDate}
        selectsRange
        maxDate={new Date()}
      />
    </>
  );
}

export default RangeDatePicker;
